import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  Layers,
  MailCheck,
  RotateCcw,
  Save,
  UserPlus,
  Users,
  UserX,
  Flag,
} from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { FormSection } from '@/components/ui/FormSection';
import { Modal } from '@/components/ui/Modal';
import { SecureImage } from '@/components/ui/SecureImage';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { NOT_ASSIGNED_LABEL } from '@/constants/houses';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { SetHouseModal } from '@/features/institute/houses/components/SetHouseModal';
import { useInstituteBranding } from '@/features/institute/store/branding.store';
import { classesApi } from '@/features/institute/classes/api/classes.api';
import { CONSOLE_PALETTE, CONSOLE_PANEL_SKIN } from '@/features/institute/theme';
import { admissionsApi } from '@/features/institute/admissions/api/admissions.api';
import {
  AdmissionProgressRail,
  type SectionProgress,
} from '@/features/institute/admissions/components/AdmissionProgressRail';
import {
  AddressSection,
  DocumentsSection,
  FamilySection,
  PersonalSection,
  PlacementSection,
  TransportSection,
} from '@/features/institute/admissions/components/AdmissionSections';
import { BulkImportSteps } from '@/features/institute/admissions/components/BulkImportPanel';
import { FeeSection } from '@/features/institute/admissions/components/FeeSection';
import {
  PARENT_ACCOUNT_NOTE,
  requiredDocuments,
  STUDENT_ACCOUNT_NOTE,
  type DocumentKind,
} from '@/features/institute/admissions/constants';
import {
  admissionFormResolver,
  admissionSchema,
  type AdmissionFormValues,
} from '@/features/institute/admissions/schemas/admission.schema';
import {
  ADMISSION_SECTIONS,
  isFilled,
  SECTION_SCROLL_MARGIN,
  sectionAnchorId,
  type AdmissionField,
  type AdmissionSectionSpec,
} from '@/features/institute/admissions/sections';
import { EMPTY_ADMISSION_FORM } from '@/features/institute/admissions/utils';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { applyFieldErrors } from '@/lib/forms';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { ImportResult } from '@/types/institute';
import type { Student, StudentStatus } from '@/types/student';
import { studentsApi } from '../api/students.api';
import { CollegeStudentForm } from '../components/CollegeStudentForm';
import { StudentDocumentsPanel } from '../components/StudentDocumentsPanel';
import {
  STUDENT_STATUS_LABEL,
  studentStatusOptions,
  STUDENT_STATUS_TONE,
  SUSPEND_EXPLAINER,
} from '../constants';
import { registerPathFrom, retiredAdmissionTypeLabel, toAdmissionForm, toUpdatePayload } from '../utils';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';

/**
 * One child's record — taking it, and correcting it.
 *
 * **One component for both, because it is one form.** A student is described by
 * the same seven sections whether the office is admitting them or fixing a
 * spelling a year later, and two screens describing one child is how an
 * institute ends up able to enter a middle name at admission and not afterwards
 * — or how the redesign lands on one of them and not the other, which is what
 * happened here (CLAUDE.md §3).
 *
 * Which job it is doing comes from the route and nothing else:
 *
 *   `/admin/students/new`     no uuid, so a blank form and a create
 *   `/admin/students/:uuid`   a uuid, so the record is loaded and a patch
 *
 * Three things the form does not offer once a student exists, because they are
 * not corrections:
 *
 *   the admission number  issued once from the institute's own series
 *   the academic year     the session decides it, not this screen
 *   the fees              what a family was charged is a financial record;
 *                         changing it is a fee transaction and belongs to the
 *                         fee module. Editable at admission, read-only after.
 *
 * A college is a different form rather than this one with two thirds hidden: it
 * admits an adult on their own details, with no class, section, guardian or fee
 * record, and an institute form with those fields hidden would still be
 * validated as an institute's underneath.
 */
export function StudentFormPage() {
  const { uuid } = useParams<{ uuid: string }>();
  const isEdit = Boolean(uuid);

  const navigate = useNavigate();
  const permissions = useAuthStore((state) => state.user?.permissions);
  const canManage = Boolean(permissions?.includes('STUDENT_MANAGE'));

  const branding = useInstituteBranding((state) => state.branding);
  const isCollege = branding?.institutionType === 'COLLEGE';

  // A college has no classes to choose from, so the call is skipped rather than
  // sent to be answered with an empty list.
  const {
    data: classes,
    isLoading: isLoadingClasses,
    error: classesError,
    refetch: refetchClasses,
  } = useApiQuery(() => (isCollege ? Promise.resolve([]) : classesApi.list()), [isCollege]);

  const {
    data: student,
    isLoading: isLoadingStudent,
    error: studentError,
    refetch: refetchStudent,
  } = useApiQuery(
    () => (uuid ? studentsApi.get(uuid) : Promise.resolve(null)),
    [uuid],
  );

  // Arriving from a roster's "documents pending" link (`#documents`): go
  // straight to the papers once the record has drawn, not the top of the form.
  const { hash, state: locationState } = useLocation();
  // The register as it was left — its class, section, search and page — when
  // this record was opened from it; the whole register otherwise.
  const registerPath = registerPathFrom(locationState);
  useEffect(() => {
    if (hash !== '#documents' || !student || isLoadingClasses) return;
    document.getElementById(sectionAnchorId('documents'))?.scrollIntoView({ behavior: 'smooth' });
  }, [hash, student, isLoadingClasses]);

  // Suggestions only: if they fail to load, the transport boxes are still
  // plain text boxes and the form works exactly as before — so no error state.
  const { data: transportRoutes } = useApiQuery(
    () => (isCollege ? Promise.resolve([]) : studentsApi.transportOptions()),
    [isCollege],
  );

  const [photo, setPhoto] = useState<File | null>(null);
  const [documents, setDocuments] = useState<Partial<Record<DocumentKind, File>>>({});
  /** Set by the first save attempt, so missing papers are not flagged before then. */
  const [isDocumentCheckShown, setIsDocumentCheckShown] = useState(false);
  /** Bumped on every clear, to remount the parts that hold state of their own. */
  const [resetToken, setResetToken] = useState(0);
  const [status, setStatus] = useState<StudentStatus>('ACTIVE');
  /** Whether the office has chosen to replace a retired admission type on the record. */
  const [isReplacingAdmissionType, setIsReplacingAdmissionType] = useState(false);
  const [isConfirmingSuspend, setIsConfirmingSuspend] = useState(false);
  const [isChangingStanding, setIsChangingStanding] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [isInvitingParent, setIsInvitingParent] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const shouldShowDone = Boolean(
    importResult &&
    importResult.admitted > 0 &&
    importResult.rejected === 0
  );

  const form = useForm<AdmissionFormValues>({
    resolver: admissionFormResolver,
    defaultValues: EMPTY_ADMISSION_FORM,
  });
  useLiveFormErrors(form);
  const {
    formState: { errors, isSubmitting, isDirty },
  } = form;

  // Fill the form once the record it is correcting has arrived. Admitting starts
  // on `EMPTY_ADMISSION_FORM` and is left alone.
  useEffect(() => {
    if (!student) return;
    form.reset(toAdmissionForm(student));
    setStatus(student.status);
    setIsReplacingAdmissionType(false);
  }, [student, form]);

  function handleDocumentChange(kind: DocumentKind, file: File | null) {
    setDocuments((current) => {
      const next = { ...current };
      if (file) next[kind] = file;
      else delete next[kind];
      return next;
    });
  }

  /**
   * Where an import lands.
   *
   * Nothing left to fix means the job is done, so the office is taken to the
   * roll — that is what it came here to change, and leaving it on a blank
   * admission form makes it look as though nothing happened. A sheet with
   * rejected rows keeps its place, because the results table is the only copy
   * of which rows failed and why.
   */
  function handleImported(_result: ImportResult) {
    // Keep dialog open so the user can review the results table.
  }

  /** Back to blank when admitting, back to the stored record when correcting. */
  function resetForm() {
    if (student) {
      form.reset(toAdmissionForm(student));
      setStatus(student.status);
      setIsReplacingAdmissionType(false);
      return;
    }

    form.reset(EMPTY_ADMISSION_FORM);
    setPhoto(null);
    setDocuments({});
    setIsDocumentCheckShown(false);
    setResetToken((token) => token + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /**
   * Sends the student the link that lets them into the student panel.
   *
   * Opens the account too if their admission could not — a child admitted
   * before they had an email address has no login until somebody asks for one.
   * No password is sent or shown: they set their own from the link.
   */
  async function sendInvite() {
    if (!uuid) return;
    setIsInviting(true);

    try {
      await studentsApi.resendInvite(uuid);
      toast.success(
        'A login link is on its way',
        'They can set their own password with it. Any earlier link has stopped working.',
      );
    } catch (caught) {
      // The server names the reason — no address on the record, or one that
      // already signs in here — so it is shown verbatim.
      toast.error(
        'Could not send that link',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsInviting(false);
    }
  }

  /**
   * Sends the child's parent the link into the parent panel.
   *
   * A different account from the student's — the two panels are different
   * products — so a separate button rather than a second address on the same
   * one. For a young child this is the login the family actually uses.
   */
  async function sendGuardianInvite() {
    if (!uuid) return;
    setIsInvitingParent(true);

    try {
      // Only the server can read the encrypted addresses on the record, so it
      // names the contact it chose and the toast repeats it back.
      const { sentTo } = await studentsApi.resendGuardianInvite(uuid);
      toast.success(
        `A parent login link is on its way to ${sentTo}`,
        'They can set their own password with it. Any earlier link has stopped working.',
      );
    } catch (caught) {
      // The server names the reason — no contact with an address, or one that
      // already signs in here — so it is shown verbatim.
      toast.error(
        'Could not send that link',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsInvitingParent(false);
    }
  }

  /**
   * Suspends or reinstates, then stays on the record: the page redraws with the
   * new standing, and the header offers the way back.
   */
  async function changeStanding(action: 'suspend' | 'reinstate') {
    if (!uuid || !student) return;
    setIsChangingStanding(true);

    try {
      if (action === 'suspend') {
        await studentsApi.suspend(uuid);
        toast.success(
          `${student.fullName} has been suspended`,
          'Use Reactivate on this page to bring them back.',
        );
      } else {
        await studentsApi.reinstate(uuid);
        toast.success(`${student.fullName} is back on the register`);
      }
      refetchStudent();
    } catch (caught) {
      toast.error(
        action === 'suspend' ? 'Could not suspend this student' : 'Could not reactivate this student',
        caught instanceof ApiRequestError ? caught.message : 'Please try again.',
      );
    } finally {
      setIsConfirmingSuspend(false);
      setIsChangingStanding(false);
    }
  }

  // A retired quota type on the record (RTE, management, staff ward). Null when
  // admitting, or when the form can show the stored type as it is.
  const retiredTypeLabel = student ? retiredAdmissionTypeLabel(student) : null;

  const onSubmit = form.handleSubmit(
    async (values) => {
      const parsed = admissionSchema.parse(values);

      // Files are not form fields, so the resolver cannot hold them back. Each
      // missing one already shows its message; take the office to them.
      if (!isEdit && missingDocuments.length > 0) {
        toast.error(
          'Could not register this admission',
          'Attach the documents marked as needed for this admission.',
        );
        document.getElementById(sectionAnchorId('documents'))?.scrollIntoView({ behavior: 'smooth' });
        return;
      }

      try {
        if (uuid && student) {
          const saved = await studentsApi.update(
            uuid,
            toUpdatePayload(parsed, status, {
              keepAdmissionType: retiredTypeLabel !== null && !isReplacingAdmissionType,
            }),
          );

          /**
           * The photograph travels on its own route, after the correction.
           *
           * Its own request because it is bytes rather than a field, and second
           * because a patch the server rejects must not leave a file stored
           * against a record that never took the change. A photograph that
           * fails on its own is reported as exactly that — the record *was*
           * saved, and telling the office otherwise sends them to re-enter a
           * form that is already correct.
           */
          if (photo) {
            try {
              await studentsApi.uploadPhoto(uuid, photo);
              setPhoto(null);
              // Remounts the picker so it drops its own preview and draws the
              // stored photograph instead.
              setResetToken((token) => token + 1);
            } catch (caught) {
              toast.error(
                `${saved.fullName}'s record is saved, but the photograph is not`,
                caught instanceof ApiRequestError
                  ? caught.message
                  : 'Please try the photograph again.',
              );
              refetchStudent();
              return;
            }
          }

          toast.success(`${saved.fullName}'s record is updated`, 'The correction is recorded against your account.');
          refetchStudent();
          return;
        }

        const admitted = await admissionsApi.create(parsed, photo, documents);
        toast.success(
          `${admitted.fullName} is admitted`,
          [
            `Admission number ${admitted.admissionNumber} — ${admitted.className} ${admitted.sectionName}.`,
            STUDENT_ACCOUNT_NOTE[admitted.account],
            PARENT_ACCOUNT_NOTE[admitted.parentAccount],
          ]
            .filter(Boolean)
            .join(' '),
        );
        resetForm();
      } catch (caught) {
        if (caught instanceof ApiRequestError) {
          // Field errors stay on the fields: a toast that fades cannot be the
          // only place someone is told which box to fix.
          applyFieldErrors(form.setError, caught);
          toast.error(
            isEdit ? 'Could not save this record' : 'Could not register this admission',
            caught.message,
          );
          // The message is under the box now, so take them to the first one.
          const [firstField] = Object.keys(caught.fieldMap);
          if (firstField) form.setFocus(firstField as AdmissionField);
          return;
        }
        toast.error(
          isEdit ? 'Could not save this record' : 'Could not register this admission',
          'Something went wrong. Please try again.',
        );
      }
    },
    // A rejected submit needs no handler: react-hook-form puts the cursor in
    // the first box that failed, and its message is drawn right under it.
  );

  /**
   * How complete the record is, section by section.
   *
   * Watched rather than read once, so the rail moves as somebody types. Useful
   * on both jobs and for the same reason: admitting, it says what is left
   * before this can be saved; correcting, it says which parts of an old record
   * were never filled in.
   */
  const values = form.watch();

  // Recomputed as the type and category change, so the labels and messages on
  // the documents panel follow what has been chosen above.
  const documentsNeeded = requiredDocuments(values);
  const missingDocuments = documentsNeeded.filter((kind) => !documents[kind]);
  const documentErrors: Partial<Record<DocumentKind, string>> = isDocumentCheckShown
    ? Object.fromEntries(missingDocuments.map((kind) => [kind, 'Required for this admission']))
    : {};

  const requiredOf = (spec: AdmissionSectionSpec) => spec.requiredFor?.(values) ?? spec.required;

  const sectionProgress: SectionProgress[] = ADMISSION_SECTIONS.map((spec) =>
    // The documents panel is counted by file rather than by field, and only
    // when admitting — the edit screen does not show it.
    spec.key === 'documents' && !isEdit
      ? {
          key: spec.key,
          done: documentsNeeded.length - missingDocuments.length,
          total: documentsNeeded.length,
          errors: isDocumentCheckShown ? missingDocuments.length : 0,
        }
      : {
          key: spec.key,
          done: requiredOf(spec).filter((field) => isFilled(values[field])).length,
          total: requiredOf(spec).length,
          errors: spec.fields.filter((field) => errors[field]).length,
        },
  );

  const overall = sectionProgress.reduce(
    (running, section) => ({
      done: running.done + section.done,
      total: running.total + section.total,
    }),
    { done: 0, total: 0 },
  );

  const isReady = overall.total > 0 && overall.done === overall.total;

  /** Every state of this screen is drawn in the console's own frame. */
  const shell = cn('flex flex-col gap-6 animate-hero-zoom', CONSOLE_PALETTE);

  /**
   * What may be done on this screen header.
   */
  /** Placing this student in a house, from the record itself. */
  const [isSettingHouse, setIsSettingHouse] = useState(false);
  const houseModal = student ? (
    <SetHouseModal
      isOpen={isSettingHouse}
      students={[{ uuid: student.uuid, fullName: student.fullName, houseUuid: student.house?.uuid ?? null }]}
      onClose={() => setIsSettingHouse(false)}
      onSaved={() => {
        setIsSettingHouse(false);
        refetchStudent();
      }}
    />
  ) : null;

  const headerActions = (
    <div className="flex flex-wrap items-center gap-2">
      {!isEdit && canManage && (
        <button
          type="button"
          onClick={() => setIsImporting(true)}
          className="inline-flex items-center gap-2 px-3.5 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs sm:text-sm font-semibold shadow-xs hover:shadow-md transition-all cursor-pointer"
        >
          <Download className="w-4 h-4 text-slate-500 dark:text-slate-400" />
          <span>Import students</span>
        </button>
      )}
      {isEdit && student && canManage && (
        <>
          <button
            type="button"
            onClick={() => setIsSettingHouse(true)}
            className="inline-flex items-center gap-2 px-3.5 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs sm:text-sm font-semibold shadow-xs hover:shadow-md transition-all cursor-pointer"
          >
            <Flag className="w-4 h-4 text-slate-500 dark:text-slate-400 shrink-0" />
            <span>{student.house ? 'Change house' : 'Assign house'}</span>
          </button>
          <button
            type="button"
            disabled={isInviting}
            onClick={() => void sendInvite()}
            className="inline-flex items-center gap-2 px-3.5 py-2 border border-blue-200 dark:border-blue-800/80 bg-blue-50/80 dark:bg-blue-950/50 hover:bg-blue-100 dark:hover:bg-blue-900/60 text-blue-700 dark:text-blue-300 rounded-xl text-xs sm:text-sm font-semibold shadow-xs hover:shadow-md transition-all cursor-pointer disabled:opacity-50"
          >
            <MailCheck className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
            <span>{isInviting ? 'Sending link…' : 'Send student link'}</span>
          </button>
          <button
            type="button"
            disabled={isInvitingParent}
            onClick={() => void sendGuardianInvite()}
            className="inline-flex items-center gap-2 px-3.5 py-2 border border-blue-200 dark:border-blue-800/80 bg-blue-50/80 dark:bg-blue-950/50 hover:bg-blue-100 dark:hover:bg-blue-900/60 text-blue-700 dark:text-blue-300 rounded-xl text-xs sm:text-sm font-semibold shadow-xs hover:shadow-md transition-all cursor-pointer disabled:opacity-50"
          >
            <Users className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
            <span>{isInvitingParent ? 'Sending link…' : 'Send parent link'}</span>
          </button>
          {student.status === 'SUSPENDED' ? (
            <button
              type="button"
              onClick={() => void changeStanding('reinstate')}
              disabled={isChangingStanding}
              className="inline-flex items-center gap-2 px-3.5 py-2 border border-emerald-200 dark:border-emerald-800/80 bg-emerald-50/80 dark:bg-emerald-950/50 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300 rounded-xl text-xs sm:text-sm font-semibold shadow-xs hover:shadow-md transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <RotateCcw className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <span>{isChangingStanding ? 'Reactivating…' : 'Reactivate'}</span>
            </button>
          ) : (
            (student.status === 'ACTIVE' || student.status === 'INACTIVE') && (
              <button
                type="button"
                onClick={() => setIsConfirmingSuspend(true)}
                className="inline-flex items-center gap-2 px-3.5 py-2 border border-rose-200 dark:border-rose-800/80 bg-rose-50/80 dark:bg-rose-950/50 hover:bg-rose-100 dark:hover:bg-rose-900/60 text-rose-700 dark:text-rose-300 rounded-xl text-xs sm:text-sm font-semibold shadow-xs hover:shadow-md transition-all cursor-pointer"
              >
                <UserX className="w-4 h-4 text-rose-600 dark:text-rose-400 shrink-0" />
                <span>Suspend</span>
              </button>
            )
          )}
        </>
      )}
    </div>
  );

  const suspendDialog = student ? (
    <ConfirmDialog
      isOpen={isConfirmingSuspend}
      tone="danger"
      title={`Suspend ${student.fullName}?`}
      description={SUSPEND_EXPLAINER}
      confirmLabel="Suspend student"
      isLoading={isChangingStanding}
      onConfirm={() => void changeStanding('suspend')}
      onCancel={() => setIsConfirmingSuspend(false)}
    />
  ) : null;

  // --- the college's own short form ----------------------------------------

  if (isCollege) {
    if (isEdit && isLoadingStudent) return <LoadingShell shell={shell} isEdit isCollege />;

    if (isEdit && (studentError || !student)) {
      return (
        <div className={shell}>
          <StudentFormHeader isEdit isCollege onBack={() => navigate(registerPath)} />
          <Alert tone="critical" title="Could not load this record">
            {studentError?.message ?? 'That student is not on your roll.'}{' '}
            <button type="button" onClick={refetchStudent} className="font-[560] underline">
              Try again
            </button>
          </Alert>
        </div>
      );
    }

    return (
      <div className={shell}>
        <StudentFormHeader
          isEdit={isEdit}
          isCollege
          student={student}
          onBack={() => navigate(registerPath)}
          actions={headerActions}
        />
        {houseModal}

        <CollegeStudentForm
          student={student ?? null}
          canManage={canManage}
          onSaved={refetchStudent}
        />

        {student && suspendDialog}

        <Modal
          isOpen={isImporting}
          onClose={() => {
            setIsImporting(false);
            setImportResult(null);
          }}
          title="Import students from a spreadsheet"
          description="Bring a whole class onto the register in one go."
          size="lg"
          footer={
            shouldShowDone ? (
              <button
                type="button"
                onClick={() => {
                  setIsImporting(false);
                  setImportResult(null);
                  navigate(registerPath);
                }}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold transition-colors cursor-pointer"
              >
                Done
              </button>
            ) : undefined
          }
        >
          {isImporting && (
            <BulkImportSteps
              onImported={handleImported}
              onResultChange={setImportResult}
            />
          )}
        </Modal>
      </div>
    );
  }

  // --- loading / error / empty ---------------------------------------------

  if (isLoadingClasses || (isEdit && isLoadingStudent)) {
    return <LoadingShell shell={shell} isEdit={isEdit} />;
  }

  if (isEdit && (studentError || !student)) {
    return (
      <div className={shell}>
        <StudentFormHeader isEdit onBack={() => navigate(registerPath)} />
        <Alert tone="critical" title="Could not load this record">
          {studentError?.message ?? 'That student is not on your roll.'}{' '}
          <button type="button" onClick={refetchStudent} className="font-[560] underline">
            Try again
          </button>
        </Alert>
      </div>
    );
  }

  if (classesError) {
    return (
      <div className={shell}>
        <StudentFormHeader isEdit={isEdit} onBack={() => navigate(registerPath)} />
        <Alert tone="critical" title="Could not load your classes">
          A student record names the class they sit in.{' '}
          <button type="button" onClick={refetchClasses} className="font-[560] underline">
            Try again
          </button>
        </Alert>
      </div>
    );
  }

  // Only blocks an admission: an existing record still has to be readable and
  // correctable at an institute whose class list is being rebuilt.
  if (!isEdit && (!classes || classes.length === 0)) {
    return (
      <div className={shell}>
        <StudentFormHeader isEdit={false} onBack={() => navigate(registerPath)} />
        <div className="card">
          <EmptyState
            icon={<Layers className="h-5 w-5" />}
            title="Set up a class first"
            description="An admission places a child in a class and charges what that class costs. Once a class and its sections exist, this form can be used."
            action={
              <Button
                leftIcon={<Layers className="h-4 w-4" />}
                onClick={() => navigate('/admin/classes')}
              >
                Go to classes and sections
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  // --- the form ------------------------------------------------------------

  return (
    <div className={shell}>
      <StudentFormHeader
        isEdit={isEdit}
        student={student}
        session={classes?.[0]?.academicYear.name}
        classCount={classes?.length}
        onBack={() => navigate(registerPath)}
        actions={headerActions}
      />
      {houseModal}

      {!canManage && (
        <Alert tone="info" title="You have read-only access">
          You can see this record but not change it. Ask an administrator to grant your role
          permission to correct a student record.
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12 animate-slide-down delay-75">
        {/* The rail. Sticky on a desktop, because this form runs well past a
            screen and a progress figure that has scrolled away is no progress
            figure at all. */}
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="lg:sticky lg:top-6">
            <AdmissionProgressRail sections={sectionProgress} overall={overall} />
          </div>
        </aside>

        <div className="flex flex-col gap-5 lg:col-span-8 xl:col-span-9">
          <fieldset disabled={!canManage} className="contents">
            <form
              onSubmit={(event) => {
                // Flags missing papers on every attempt, even one the resolver
                // rejects, so the office sees all that is left in one pass.
                setIsDocumentCheckShown(true);
                return onSubmit(event);
              }}
              noValidate
              className="flex flex-col gap-5"
            >
              {/* The shared `FormSection` panels take this console's softer
                  corners here rather than in the component, so the platform
                  screens keep the ones they were drawn with. Their colours come
                  from the palette on the root, not from overrides. */}
              <div className={cn('flex flex-col gap-5', CONSOLE_PANEL_SKIN)}>
                <div id={sectionAnchorId('child')} className={SECTION_SCROLL_MARGIN}>
                  <PersonalSection
                    form={form}
                    photo={photo}
                    onPhotoChange={setPhoto}
                    currentPhoto={
                      uuid && student?.hasPhoto ? (
                        <SecureImage
                          path={studentsApi.photoPath(uuid)}
                          alt=""
                          reloadKey={resetToken}
                          className="h-full w-full rounded-md object-cover"
                        />
                      ) : undefined
                    }
                    resetToken={resetToken}
                  />
                </div>
                <div id={sectionAnchorId('placement')} className={SECTION_SCROLL_MARGIN}>
                  <PlacementSection
                    form={form}
                    classes={classes ?? []}
                    rollNumber={student?.rollNumber}
                    keepFees={isEdit}
                    retiredAdmissionType={
                      retiredTypeLabel === null
                        ? undefined
                        : {
                            label: retiredTypeLabel,
                            isReplacing: isReplacingAdmissionType,
                            onReplace: () => setIsReplacingAdmissionType(true),
                          }
                    }
                  />
                </div>
                <div id={sectionAnchorId('family')} className={SECTION_SCROLL_MARGIN}>
                  <FamilySection form={form} />
                </div>
                <div id={sectionAnchorId('address')} className={SECTION_SCROLL_MARGIN}>
                  <AddressSection form={form} />
                </div>
                <div id={sectionAnchorId('transport')} className={SECTION_SCROLL_MARGIN}>
                  <TransportSection form={form} routes={transportRoutes ?? []} />
                </div>

                <div id={sectionAnchorId('fees')} className={SECTION_SCROLL_MARGIN}>
                  {isEdit && student ? (
                    <FormSection
                      title="F. Fees"
                      description="What this child was charged when they were admitted. Changing a fee is a fee transaction, so it is not edited here."
                      columns={1}
                    >
                      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-6">
                        <FeeFigure label="Tuition" value={student.fees?.tuitionFee} />
                        <FeeFigure label="Concession" value={student.fees?.tuitionDiscount} />
                        <FeeFigure label="Books" value={student.fees?.booksFee} />
                        <FeeFigure label="Books concession" value={student.fees?.booksDiscount} />
                        <FeeFigure label="Other fees" value={student.fees?.otherFee} />
                        <FeeFigure label="Other fees concession" value={student.fees?.otherDiscount} />
                      </dl>
                    </FormSection>
                  ) : (
                    <FeeSection form={form} />
                  )}
                </div>

                {/* Papers are collected at the counter, with the admission. */}
                {!isEdit && (
                  <div id={sectionAnchorId('documents')} className={SECTION_SCROLL_MARGIN}>
                    <DocumentsSection
                      documents={documents}
                      onDocumentChange={handleDocumentChange}
                      required={documentsNeeded}
                      errors={documentErrors}
                    />
                  </div>
                )}

                {isEdit && (
                  <FormSection
                    title="G. Standing"
                    description="Whether this student is still attending. Suspending them is a separate action."
                  >
                    <Select
                      label="Status"
                      options={studentStatusOptions(student?.status)}
                      value={status}
                      onChange={(event) => setStatus(event.target.value as StudentStatus)}
                    />
                  </FormSection>
                )}

                {/* After admission the papers are files on the record, attached
                    slot by slot — the only way they reach a child who came in
                    from a spreadsheet. Saves on its own, apart from the form. */}
                {isEdit && student && (
                  <div id={sectionAnchorId('documents')} className={SECTION_SCROLL_MARGIN}>
                    <StudentDocumentsPanel
                      key={student.uuid}
                      studentUuid={student.uuid}
                      studentName={student.fullName}
                      initial={{
                        documents: student.documents,
                        missingDocuments: student.missingDocuments,
                      }}
                      canManage={canManage}
                    />
                  </div>
                )}
              </div>

              {/* The save bar rides the bottom of the viewport: this form runs
                  well past a screen, and a button that has scrolled away is a
                  button nobody presses. It also says whether the form is ready,
                  which the button's own state does not. */}
              {canManage && (
                <div className="sticky bottom-4 z-10">
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white/90 dark:bg-[#111c2e]/90 px-4 py-3 shadow-[0_18px_40px_-16px_rgba(15,23,42,0.45)] backdrop-blur-xl">
                    <p className="flex min-w-0 items-center gap-2.5 text-[12.5px] font-bold text-slate-600 dark:text-slate-300">
                      {isEdit ? (
                        <>
                          <CheckCircle2
                            className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400"
                            aria-hidden
                          />
                          {isDirty
                            ? 'Unsaved corrections. They are recorded against your account.'
                            : 'Nothing changed yet.'}
                        </>
                      ) : isReady ? (
                        <>
                          <CheckCircle2
                            className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400"
                            aria-hidden
                          />
                          Everything required is filled in. Saving issues the admission number.
                        </>
                      ) : (
                        <>
                          <span
                            className="h-2 w-2 shrink-0 rounded-full bg-amber animate-glow"
                            aria-hidden
                          />
                          {`${overall.total - overall.done} required ${
                            overall.total - overall.done === 1 ? 'detail' : 'details'
                          } left before this can be saved.`}
                        </>
                      )}
                    </p>

                    <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto justify-end">
                      <Button
                        variant="ghost"
                        onClick={resetForm}
                        disabled={isSubmitting || (isEdit && !isDirty)}
                        leftIcon={<RotateCcw className="h-4 w-4" />}
                        className="w-full sm:w-auto justify-center"
                      >
                        {isEdit ? 'Undo my changes' : 'Clear the form'}
                      </Button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="rounded-xl bg-indigo-600 tracking-wide hover:bg-indigo-700 px-6 py-2.5 text-sm font-bold text-white shadow-sm shadow-indigo-500/20 active:scale-[0.98] inline-flex cursor-pointer items-center justify-center gap-2 transition-all disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 w-full sm:w-auto"
                      >
                        {isSubmitting ? (
                          <span>{isEdit ? 'Saving…' : 'Registering…'}</span>
                        ) : (
                          <>
                            {isEdit ? (
                              <Save className="h-4 w-4 stroke-[2.5]" aria-hidden />
                            ) : (
                              <UserPlus className="h-4 w-4 stroke-[2.5]" aria-hidden />
                            )}
                            <span>{isEdit ? 'Save this record' : 'Register this student'}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </fieldset>
        </div>
      </div>

      {suspendDialog}

      <Modal
        isOpen={isImporting}
        onClose={() => {
          setIsImporting(false);
          setImportResult(null);
        }}
        title="Import students from a spreadsheet"
        description="Bring a whole class onto the register in one go."
        size="lg"
        footer={
          shouldShowDone ? (
            <button
              type="button"
              onClick={() => {
                setIsImporting(false);
                setImportResult(null);
                navigate(registerPath);
              }}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold transition-colors cursor-pointer"
            >
              Done
            </button>
          ) : undefined
        }
      >
        {isImporting && (
          <BulkImportSteps
            onImported={handleImported}
            onResultChange={setImportResult}
          />
        )}
      </Modal>
    </div>
  );
}

/** The panels, before they have anything to hold. */
function LoadingShell({
  shell,
  isEdit,
  isCollege = false,
}: {
  shell: string;
  isEdit: boolean;
  isCollege?: boolean;
}) {
  return (
    <div className={shell}>
      <StudentFormHeader isEdit={isEdit} isCollege={isCollege} />
      <div className="flex flex-col gap-6" role="status" aria-label="Loading the student form">
        {[0, 1, 2].map((panel) => (
          <div key={panel} className="card p-5">
            <Skeleton className="h-4 w-40" />
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[0, 1, 2, 3].map((field) => (
                <Skeleton key={field} className="h-11 w-full" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * What this screen is, and the way back off it.
 *
 * A plain heading rather than the wash-and-illustration banner it used to open
 * with: the register it is reached from no longer carries one either, and the
 * form below is what somebody came here to fill in.
 *
 * The record being corrected still names itself — its admission number, class
 * and session are how you know you are on the right child — but as a quiet line
 * of text rather than a row of chips over a gradient.
 */
function StudentFormHeader({
  isEdit,
  isCollege = false,
  student,
  session,
  classCount,
  onBack,
  actions,
}: {
  isEdit: boolean;
  /** A college is admitted on its own terms, so it is announced in its own words. */
  isCollege?: boolean;
  student?: Student | null;
  session?: string;
  classCount?: number;
  onBack?: () => void;
  actions?: ReactNode;
}) {
  /**
   * How the record identifies itself, for the screen that is correcting one.
   *
   * Nothing is invented: a college student has no class, and the session comes
   * from the record rather than from whichever year is current.
   */
  const meta = [
    student?.admissionNumber,
    !isCollege && student?.className ? `${student.className} ${student.sectionName ?? ''}`.trim() : null,
    student?.academicYear.name,
  ].filter(Boolean);

  return (
    <header className="flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {onBack && (
          <div className="flex items-center gap-2 text-sm font-medium mb-1">
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-1.5 text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400 hover:underline transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Students</span>
            </button>
            <span className="text-slate-300 dark:text-slate-600 font-normal">/</span>
            <span className="text-slate-900 dark:text-white font-bold">
              {isEdit ? 'Edit student' : isCollege ? 'Admit student' : 'Add student'}
            </span>
          </div>
        )}

        <h1 className="mt-1 text-2xl font-[700] tracking-tight text-ink">
          {isEdit
            ? (student?.fullName ?? 'Student record')
            : isCollege
              ? 'Admit a student'
              : 'New admission'}
        </h1>

        {isEdit && student ? (
          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-ink-2">
            <span className="font-mono text-xs text-ink-3">{meta.join(' · ')}</span>
            <Badge tone={STUDENT_STATUS_TONE[student.status]}>
              {STUDENT_STATUS_LABEL[student.status]}
            </Badge>
            <span className="inline-flex items-center gap-1 text-xs">
              <Flag className="h-3.5 w-3.5 text-ink-3" aria-hidden />
              House:{' '}
              <span className={student.house ? 'font-semibold text-ink' : 'text-ink-3'}>
                {student.house?.name ?? NOT_ASSIGNED_LABEL}
              </span>
            </span>
          </p>
        ) : (
          <p className="mt-1 max-w-xl text-sm text-ink-2">
            {isCollege
              ? "A college admission takes the student's own details. Course placement comes later."
              : `Register a child into ${classCount ? `one of ${classCount} classes` : 'a class'}${session ? ` for ${session}` : ''}. The admission number is issued when you save.`}
          </p>
        )}
      </div>

      {actions}
    </header>
  );
}

function FeeFigure({ label, value }: { label: string; value: string | undefined }) {
  return (
    <div>
      <dt className="lbl">{label}</dt>
      <dd className="mt-0.5 font-mono text-base text-ink">
        {value ? formatCurrency(Number(value)) : '—'}
      </dd>
    </div>
  );
}
